import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

import { AUTH_TOKEN, filterBoolean } from '@recruitee/common';
import {
  HTTP_STATUS,
  LoadReferralsConditions,
  LoadReferralsOfferAssignments,
  LoadReferralsProfile,
  LoadReferralsProgram,
  LoadReferralsTerms,
  ReferralsPortalProfileRepo,
  ReferralsPortalProgramRepo,
} from '@recruitee/http';
import {
  I18nService,
  PhraseAppTranslations,
  PhraseAppTranslationService,
  phraseAppTranslationsLoader,
} from '@recruitee/i18n';

import { appTranslations, uiTranslations } from '../locales';
import { ToggleForeignAdmin } from './core/state/core.actions';

@Injectable({ providedIn: 'root' })
export class AppInitializationService {
  private protectedDataLoaded$: Subject<boolean> = new Subject();
  private publicDataLoaded$: Subject<boolean> = new Subject();

  constructor(
    private store: Store,
    private router: Router,
    private i18nService: I18nService,
    @Inject(AUTH_TOKEN)
    private authToken: string,
    private phraseAppTranslationService: PhraseAppTranslationService,
  ) {}

  public init(): void {
    let languageReadyObservables = [this.publicDataLoaded$];

    this.loadInitialPublicData();
    if (this.authToken) {
      this.loadInitialProtectedData();
      languageReadyObservables = [...languageReadyObservables, this.protectedDataLoaded$];
    }

    combineLatest(languageReadyObservables)
      .pipe(first())
      .subscribe(([publicDataLoaded, protectedDataLoaded]) => {
        const isAuthorized = publicDataLoaded && protectedDataLoaded;
        this.resolveLanguage(isAuthorized).subscribe(() => {
          this.router.initialNavigation();
        });
      });
  }

  private loadInitialProtectedData(): void {
    merge(
      this.store.dispatch(new LoadReferralsProfile({ active: true })),
      this.store.dispatch(new LoadReferralsConditions()),
      this.store.dispatch(new LoadReferralsOfferAssignments()),
    ).subscribe({
      next: () => this.protectedDataLoaded$.next(true),
      error: (err: HttpErrorResponse) => {
        if (err.status === HTTP_STATUS.UNAUTHORIZED) {
          this.resolveLanguage(false)
            .pipe(filter(Boolean))
            .subscribe(() => {
              this.protectedDataLoaded$.next(false);
              this.store.dispatch(new ToggleForeignAdmin());
              this.router.navigate(['/auth', 'sign_in'], {
                queryParams: {
                  error: 'unauthorized',
                },
              });
            });
        }
      },
    });
  }

  private loadInitialPublicData(): void {
    combineLatest([
      this.store.dispatch(new LoadReferralsProgram()),
      this.store.dispatch(new LoadReferralsTerms()),
    ]).subscribe({
      next: () => this.publicDataLoaded$.next(true),
      error: (err: HttpErrorResponse) => {
        this.resolveLanguage(false)
          .pipe(filter(Boolean))
          .subscribe(() => {
            this.router.navigate(['/error', HTTP_STATUS.NOT_FOUND]);
            this.router.initialNavigation();
          });
      },
    });
  }

  private resolveLanguage(isAuthorized: boolean): Observable<PhraseAppTranslations[]> {
    let languageObservable: Observable<string>;

    if (isAuthorized) {
      languageObservable = this.store.select(ReferralsPortalProfileRepo.activeEntity).pipe(
        filterBoolean,
        map(({ preferredLanguage }) => preferredLanguage),
      );
    } else {
      languageObservable = this.store
        .select(ReferralsPortalProgramRepo.data)
        .pipe(map(data => (data && data.companyLanguage) || 'en'));
    }

    return languageObservable.pipe(
      switchMap(async language => {
        this.i18nService.locale = language;
        const translations = await phraseAppTranslationsLoader(language, 'en', [
          appTranslations,
          uiTranslations,
        ]);
        this.phraseAppTranslationService.setTranslations(translations);
        return translations;
      }),
    );
  }
}
